import React, { useState, useEffect, useContext, useRef } from "react";
import {
  Modal,
  Button,
  Select,
  Input,
  Form,
  Table,
  Row,
  Col,
  Typography,
  Popconfirm,
} from "antd";
import { IoCloudDoneOutline } from "react-icons/io5";
import { getConvertedValue } from "utils/helpers";

import API_KEY from "api";
const { Text, Title } = Typography;
const { Option } = Select;
const EditableContext = React.createContext(null);
const EditableRow = ({ index, ...props }) => {
  const [form] = Form.useForm();
  return (
    <Form form={form} component={false}>
      <EditableContext.Provider value={form}>
        <tr {...props} />
      </EditableContext.Provider>
    </Form>
  );
};

const EditableCell = ({
  title,
  editable,
  children,
  dataIndex,
  record,
  handleSave,
  type,
  defaultValue,
  variantData,
  collectionProductId,
  setSaving,
  setIsSizeChanged,
  isSizeChanged,
  ...restProps
}) => {
  const [editing, setEditing] = useState(false);
  const inputRef = useRef(null);
  const form = useContext(EditableContext);
  useEffect(() => {
    if (editing) {
      inputRef.current.focus();
    }
  }, [editing]);

  useEffect(() => {
    if (isSizeChanged && type === "size") {
      setIsSizeChanged(false);
      form.setFieldsValue({
        [dataIndex + ":length"]: record[dataIndex]?.[1],
        [dataIndex + ":width"]: record[dataIndex]?.[2],
        [dataIndex + ":height"]: record[dataIndex]?.[3],
        [dataIndex + ":unit"]: record[dataIndex]?.[4],
      });

      handleSave(record);
      patchAndPostAttributes();
    }
  }, [isSizeChanged]);

  useEffect(() => {
    if (type === "price") {
      form.setFieldsValue({
        priceOnly: record[dataIndex]?.[1],
      });
      form.setFieldsValue({
        Price: record[dataIndex],
      });
      return;
    }
    if (type === "size") {
      form.setFieldsValue({
        [dataIndex + ":length"]: record[dataIndex]?.[1],
        [dataIndex + ":width"]: record[dataIndex]?.[2],
        [dataIndex + ":height"]: record[dataIndex]?.[3],
        [dataIndex + ":unit"]: record[dataIndex]?.[4],
      });
      return;
    }
    if (type === "csv") {
      if (record[dataIndex]) {
        form.setFieldsValue({
          [dataIndex]:
            typeof record[dataIndex] === "string"
              ? record[dataIndex].split(",")
              : record[dataIndex],
        });
        return;
      } else {
        form.setFieldsValue({
          [dataIndex]: [],
        });
        return;
      }
    }

    form.setFieldsValue({
      [dataIndex]: record?.[dataIndex],
    });
  }, [dataIndex, record, type, form]);

  const toggleEdit = () => {
    setEditing(!editing);

    if (type === "price") {
      form.setFieldsValue({
        priceOnly: record[dataIndex]?.[1],
      });
      form.setFieldsValue({
        Price: record[dataIndex],
      });
      return;
    } else if (type === "csv") {
      if (record[dataIndex]) {
        form.setFieldsValue({
          [dataIndex]:
            typeof record[dataIndex] === "string"
              ? record[dataIndex].split(",")
              : record[dataIndex],
        });
        return;
      } else {
        form.setFieldsValue({
          [dataIndex]: [],
        });
        return;
      }
    }
  };

  const saveComposite = async (value, key) => {
    try {
      // const values = await form.validateFields();
      if (type === "size") {
        const size = form.getFieldValue("size");

        let currentValues = record[dataIndex] || [null, null, null, null, null];

        const keyMap = {
          length: 1,
          width: 2,
          height: 3,
        };
        currentValues[keyMap[key]] = value;
        currentValues[0] = getCompositeValue(currentValues);

        form.setFieldsValue({ dataIndex: currentValues });

        record[dataIndex] = currentValues;
        handleSave({ ...record, dataIndex: currentValues });
        patchAndPostAttributes();
      }
    } catch (e) {}
  };

  const save = async () => {
    try {
      const values = await form.validateFields();
      toggleEdit();
      if (type === "price") {
        const price = form.getFieldValue("Price");
        price[1] = values.priceOnly;

        form.setFieldsValue({
          Price: price,
        });
      }

      const keys = Object.keys(record);
      const names = keys.filter((key) => key.includes(dataIndex));
      const attrValueId = names?.[1].split("/")?.[1];

      handleSave({ ...record, ...values }, dataIndex, type);

      const attributeId = dataIndex.split(":")?.[1];

      let editedValue;
      let compositeValue;

      if (type === "price") {
        editedValue = record[dataIndex]?.[1];
        compositeValue = {
          currency: record[dataIndex]?.[0],
          unit: record[dataIndex]?.[2],
        };
      } else {
        editedValue = values[dataIndex];
      }

      let requestPayloadForPatch = {
        id: attrValueId,
        attribute_id: attributeId,
        values:
          editedValue && typeof editedValue === "object"
            ? editedValue.join(",")
            : editedValue,
        composite: compositeValue,
      };

      if (attrValueId) {
        setSaving(true);
        API_KEY.URL.patch(
          `${API_KEY.path.attribute_values}/${attrValueId}`,
          requestPayloadForPatch
        )
          .then((res) => {
            setSaving(false);
          })
          .catch((err) => {
            setSaving(false);
          });
      }
    } catch (errInfo) {
      console.log("Save failed:", errInfo);
    }
  };
  const getCompositeValue = (values) => {
    const dimensionMap = {
      length: "L",
      width: "B",
      height: "H",
    };
    let size = {
      length: values[1],
      width: values[2],
      height: values[3],
    };

    let unit = values[4] || "";

    const { length = "", width = "", height = "" } = size;
    let sizeKeys = Object.keys(size);
    const validSizes = [length, width, height].filter(Boolean) || [];
    const validKeys = sizeKeys.filter((item) => size[item] && item !== "unit");
    const dimensions = validKeys.map((item) => dimensionMap[item]) || [];

    return `${validSizes.join("x")} ${unit} (${dimensions.join("x")})`;
  };

  const patchAndPostAttributes = () => {
    const keys = Object.keys(record);
    const names = keys.filter((key) => key.includes(dataIndex));
    const attrValueId = names?.[1].split("/")?.[1];

    const attributeId = dataIndex.split(":")?.[1];

    let editedValue = record[dataIndex]?.[0];
    let compositeValue = {
      length: record[dataIndex]?.[1] || "",
      width: record[dataIndex]?.[2] || "",
      height: record[dataIndex]?.[3] || "",
      unit: record[dataIndex]?.[4] || "",
    };

    let requestPayloadForPatch = {
      id: attrValueId,
      attribute_id: attributeId,
      values: editedValue,
      collections_products_id: record?.productId,
      composite: compositeValue,
    };
    let requestPayloadForPost = {
      collections_products_id: record?.collection_product_id,
      attribute_values_id: {
        attribute_id: attributeId,
        values: editedValue,
        composite: compositeValue,
      },
    };

    if (attrValueId) {
      setSaving(true);
      API_KEY.URL.patch(
        `${API_KEY.path.attribute_values}/${attrValueId}`,
        requestPayloadForPatch
      )
        .then((res) => {
          setSaving(false);
        })
        .catch((err) => {
          setSaving(false);
        });
    }
  };

  const renderFields = (type) => {
    const componentsObj = {
      text: <Input ref={inputRef} onBlur={save} onPressEnter={save} />,
      csv: (
        <Select
          mode="tags"
          ref={inputRef}
          onChange={save}
          // onBlur={save}
          onPressEnter={save}
          //   onChange={save}
          notFoundContent={<Text type="secondary">Start typing to add...</Text>}
        >
          {defaultValue?.csv?.map(
            (item, index) =>
              item && (
                <Option key={`${index}${item}`} value={item}>
                  {item}
                </Option>
              )
          )}
        </Select>
      ),
      price: (
        <Input
          onWheel={(e) => e.currentTarget.blur()}
          onBlur={save}
          onPressEnter={save}
          size="large"
          ref={inputRef}
          style={{ minWidth: 300 }}
          addonBefore={<div ref={inputRef}>{children?.[1]?.[0]}</div>}
          addonAfter={<div ref={inputRef}>{children?.[1]?.[2]}</div>}
          type="number"
          // defaultValue={children?.[1]}
        />
      ),
      size: (
        <Row style={{ minWidth: 300, marginTop: "25px" }}>
          <Col span="7">
            <Form.Item name={dataIndex + ":length"}>
              <Input
                style={{
                  borderRadius: "0",
                  borderRight: "none",
                }}
                onWheel={(e) => e.currentTarget.blur()}
                onBlur={(e) => saveComposite(e.target.value, "length")}
                name=""
                onPressEnter={(e) => saveComposite(e.target.value, "length")}
                size="medium"
                ref={inputRef}
                type="number"
                defaultValue={children?.[1]?.[1]}
              />
            </Form.Item>
          </Col>
          <Col span="7">
            <Form.Item name={dataIndex + ":width"}>
              <Input
                style={{
                  borderRadius: "0",
                  borderRight: "none",
                }}
                onWheel={(e) => e.currentTarget.blur()}
                onBlur={(e) => saveComposite(e.target.value, "width")}
                onPressEnter={(e) => saveComposite(e.target.value, "width")}
                size="medium"
                ref={inputRef}
                type="number"
                defaultValue={children?.[1]?.[2]}
              />
            </Form.Item>
          </Col>
          <Col span="10">
            <Form.Item name={dataIndex + ":height"}>
              <Input
                className="size_height"
                onWheel={(e) => e.currentTarget.blur()}
                onBlur={(e) => saveComposite(e.target.value, "height")}
                onPressEnter={(e) => saveComposite(e.target.value, "height")}
                size="medium"
                // ref={inputRef}
                addonAfter={
                  <div
                    style={{ borderRadius: "0", width: "10%" }}
                    ref={inputRef}
                  >
                    {children?.[1]?.[4]}
                  </div>
                }
                type="number"
                defaultValue={children?.[1]?.[3]}
              />
            </Form.Item>
          </Col>
        </Row>
      ),
      textarea: (
        <Input.TextArea ref={inputRef} onBlur={save} onPressEnter={save} />
      ),
      select: (
        <Select size="large" ref={inputRef} onChange={save}>
          {defaultValue && defaultValue?.select?.length > 0
            ? defaultValue?.select?.map((item, index) => (
                <Option key={`${index}${item}`} value={item}>
                  {item}{" "}
                </Option>
              ))
            : null}
        </Select>
      ),
    };
    return (
      componentsObj[type] || (
        <Input ref={inputRef} onBlur={save} onPressEnter={save} />
      )
    );
  };

  let childNode = children;

  if (editable) {
    childNode = editing ? (
      <Form.Item
        style={{
          margin: 0,
        }}
        name={type === "price" ? "priceOnly" : dataIndex}
      >
        {renderFields(type)}
      </Form.Item>
    ) : (
      <Form.Item
        style={{
          margin: 0,
        }}
        onClick={toggleEdit}
        name={type === "price" ? "priceOnly" : dataIndex}
      >
        {renderFields(type)}
      </Form.Item>
    );
  }

  return <td {...restProps}>{childNode}</td>;
};

export default function Index({
  isVariantModalVisible,
  setIsVariantModalVisible,
  handleOkVariant,
  handleCancelVariant,
  variantData,
  activeProductId,
  currency,
  setCurrency,
  unit,
  form,
  setUnit,
  sizeUnit,
  setSizeUnit,
  collectionProductId,
  customAttrs,
}) {
  const [columns, setColumns] = useState([]);
  const [dataSource, setDataSource] = useState([]);
  const [saving, setSaving] = useState(false);
  const [attrId, setAttrId] = useState(null);
  const [isSizeChanged, setIsSizeChanged] = useState(false);
  useEffect(() => {
    if (variantData.length > 0) {
      mapData(variantData);
      createColumns(variantData);
    }
  }, [variantData]);

  const staticCol = [
    {
      title: "Product ID",
      dataIndex: "productId",
      editable: false,
      width: "22%",
    },
    {
      title: "Variant ID",
      dataIndex: "variantId",
      editable: false,
      width: "22%",
    },
  ];

  const createColumns = (data) => {
    var variantCols = data.map((variant) => {
      return variant?.attributes?.map((item) => {
        let attr = item?.attribute_values_id?.attribute_id;

        let col = {
          title:
            attr?.dtype === "price" && attr?.is_internal === false ? (
              <span
                style={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "space-around",
                  justifyItems: "center",
                }}
              >
                <select
                  style={{
                    background: "transparent",
                    borderStyle: "none",
                    outlineStyle: "none",
                    outline: "none",
                  }}
                  // value={currency}
                  onChange={(e) =>
                    handleCurrencyChange(e, attr?.name, attr?.id)
                  }
                >
                  <option disabled selected value>
                    Currency
                  </option>
                  {attr?.default?.currency.map((curr, index) => (
                    <option key={index} value={curr}>
                      {curr}
                    </option>
                  ))}
                </select>
                <span>{attr?.name}</span>
                <select
                  style={{
                    background: "transparent",
                    borderStyle: "none",
                    outlineStyle: "none",
                    outline: "none",
                  }}
                  value={unit}
                  onChange={(e) => handleUnitChange(e, attr?.name, attr?.id)}
                >
                  <option disabled selected value>
                    Unit
                  </option>
                  {attr?.default?.unit.map((unit, index) => (
                    <option key={index} value={unit}>
                      {unit}
                    </option>
                  ))}
                </select>
              </span>
            ) : attr?.dtype === "size" && attr?.is_internal === false ? (
              <span
                style={{
                  color: "#828786",
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "space-around",
                  justifyItems: "center",
                }}
              >
                <span>{attr.name}</span>
                <select
                  style={{
                    background: "transparent",
                    marginRight: 4,
                    borderStyle: "none",
                    outlineStyle: "none",
                    outline: "none",
                    minWidth: 100,
                    padding: "0px 10px",
                  }}
                  onChange={(e) => {
                    handleSizeUnitChange(e, attr?.name, attr?.id);
                  }}
                  value={sizeUnit}
                >
                  <option disabled selected value>
                    Unit
                  </option>
                  {attr?.default?.unit.map((unit, index) => (
                    <option key={index} value={unit}>
                      {unit}
                    </option>
                  ))}
                </select>
              </span>
            ) : (
              <span>{attr?.name}</span>
            ),
          dataIndex: `${attr?.name}:${attr?.id}`,
          editable: true,
          type: attr?.dtype,
          defaultValue: attr?.default,
          width: "22%",
        };

        return customAttrs?.includes(`${attr?.id}`) && col;
      });
    });

    const newColumns = [...new Set(variantCols.flat())];
    const uniqueCols = newColumns.filter(
      (v, i, a) => a.findIndex((t) => t?.dataIndex === v?.dataIndex) === i
    );
    const oldIndex = uniqueCols.findIndex((i) => i.type === "price");

    const newIndex = uniqueCols.length - 1;
    function arraymove(arr, fromIndex, toIndex) {
      var element = arr[fromIndex];
      arr.splice(fromIndex, 1);
      arr.splice(toIndex, 0, element);
      return arr;
    }

    let sorted =
      oldIndex === -1 ? uniqueCols : arraymove(uniqueCols, oldIndex, newIndex);

    const cols = [...staticCol, ...sorted.flat()];
    const newCols = cols.filter((i) => i);

    setColumns(newCols);
  };

  const mapData = (data) => {
    let dataArray = [];
    data.forEach((item, index) => {
      const attrs = item?.attributes;
      // if(attrs?.length > 0) {
      let obj = attrs.map((attr) => {
        const key = `${attr?.attribute_values_id?.attribute_id?.name}:${attr?.attribute_values_id?.attribute_id?.id}`;
        const value =
          attr?.attribute_values_id?.attribute_id?.dtype === "price"
            ? [
                attr?.attribute_values_id?.composite?.currency,
                attr?.attribute_values_id?.values,
                attr?.attribute_values_id?.composite?.unit,
              ]
            : attr?.attribute_values_id?.attribute_id?.dtype === "size"
            ? [
                attr?.attribute_values_id?.values,
                attr?.attribute_values_id?.composite?.length,
                attr?.attribute_values_id?.composite?.width,
                attr?.attribute_values_id?.composite?.height,
                attr?.attribute_values_id?.composite?.unit,
              ]
            : attr?.attribute_values_id?.values;
        return {
          [key]: value,
          key: index,
          [`${key}/${attr?.attribute_values_id?.id}`]: "id",
        };
      });

      if (obj.length > 0) {
        const keys = Object.assign(...obj);
        dataArray.push({
          variantId: item?.variant_id,
          productId: activeProductId,
          ...keys,
        });
      }

      //}
    });
    setDataSource(dataArray);
  };

  const components = {
    body: {
      row: EditableRow,
      cell: EditableCell,
    },
  };
  const handleSave = (row, dataIndex, type) => {
    const newData = [...dataSource];
    const index = newData.findIndex((item) => row.key === item.key);
    const item = newData[index];
    newData.splice(index, 1, { ...item, ...row });
    setDataSource(newData);
  };

  const updateCurrencyOrUnit = (attributeId, data) => {
    for (let i = 0; i < dataSource.length; i++) {
      const item = dataSource[i];
      const keys = Object.keys(item);

      for (let j = 0; j < keys.length; j++) {
        const key = keys[j];

        if (item[key] === "id" && key.includes(`${attributeId}`)) {
          const editedValue = item[`${key.split(":")?.[0]}:${attributeId}`];
          const attrValueId = key.split("/")[1];

          let compositeValue = {
            currency: data?.currency ? data?.currency : editedValue?.[0],
            unit: data?.unit ? data?.unit : editedValue?.[2],
          };

          let requestPayloadForPatch = {
            id: attrValueId,
            attribute_id: attributeId,
            values: editedValue?.[1],
            composite: compositeValue,
          };

          if (attrValueId) {
            setSaving(true);
            API_KEY.URL.patch(
              `${API_KEY.path.attribute_values}/${attrValueId}`,
              requestPayloadForPatch
            )
              .then((res) => {
                setSaving(false);
              })
              .catch((err) => {
                setSaving(false);
              });
          }
        }
      }
    }
  };

  const handleCurrencyChange = (event, title, attributeId) => {
    let x = dataSource;
    setCurrency(event?.target?.value);
    setAttrId(attributeId);
    setDataSource((state) =>
      state.map((item) => {
        item[`${title}:${attributeId}`]?.length > 0
          ? (item[`${title}:${attributeId}`][0] = event?.target?.value)
          : (item[`${title}:${attributeId}`] = [event?.target?.value]);
        return item;
      })
    );
    // updateCurrencyOrUnit(
    //   attributeId,
    //   { currency: event?.target?.value },
    //   title
    // );
  };

  const handleSizeUnitChange = (event, title, attributeId) => {
    setSizeUnit(event?.target?.value);
    // setAttrId(attributeId);
    setDataSource((state) => {
      return state.map((item) => {
        if (item[`${title}:${attributeId}`]?.length > 0) {
          // item[`${title}:${attributeId}`][4] = event?.target?.value;
          let oldUnit = item[`${title}:${attributeId}`]?.[4] || "";
          let newUnit = event?.target?.value;
          if (oldUnit && oldUnit !== newUnit) {
            const length = getConvertedValue(
              item[`${title}:${attributeId}`]?.[1],
              newUnit
            );
            const width = getConvertedValue(
              item[`${title}:${attributeId}`]?.[2],
              newUnit
            );
            const height = getConvertedValue(
              item[`${title}:${attributeId}`]?.[3],
              newUnit
            );
            item[`${title}:${attributeId}`] = [
              `${[length, width, height].filter(Boolean).join("x")} ${
                newUnit || ""
              }`,
              length,
              width,
              height,
              newUnit,
            ];
          } else {
            if (newUnit) {
              item[`${title}:${attributeId}`][4] = newUnit;
            }
          }
        } else {
          item[`${title}:${attributeId}`] = [
            null,
            null,
            null,
            null,
            event?.target?.value,
          ];
        }
        return item;
      });
    });

    /**
     *         if (item[title]?.length > 0) {
          let oldUnit = item[title]?.[4] || "";
          let newUnit = event?.target?.value;
          if (oldUnit && oldUnit !== newUnit) {
            const length = getConvertedValue(item[title]?.[1], newUnit);
            const width = getConvertedValue(item[title]?.[2], newUnit);
            const height = getConvertedValue(item[title]?.[3], newUnit);
            item[title] = [
              `${[length, width, height].join("x")} ${newUnit || ""}`,
              length,
              width,
              height,
              newUnit,
            ];
          }
        }
     */
    updateSizeUnit(attributeId);
    setIsSizeChanged(true);
  };

  const updateSizeUnit = (attributeId) => {
    for (let i = 0; i < dataSource.length; i++) {
      const item = dataSource[i];
      const keys = Object.keys(item);

      for (let j = 0; j < keys.length; j++) {
        const key = keys[j];

        if (key.includes(`${attributeId}`)) {
          const editedValue = item[`${key.split(":")?.[0]}:${attributeId}`];
          const attrValueId = key.split("/")[1];
          let compositeValue = {
            length: editedValue?.[1],
            width: editedValue?.[2],
            height: editedValue?.[3],
            unit: editedValue?.[4],
          };

          let requestPayloadForPatch = {
            id: attrValueId,
            attribute_id: attributeId,
            values: editedValue?.[1],
            composite: compositeValue,
          };

          if (attrValueId) {
            setSaving(true);
            API_KEY.URL.patch(
              `${API_KEY.path.attribute_values}/${attrValueId}`,
              requestPayloadForPatch
            )
              .then((res) => {
                setSaving(false);
              })
              .catch((err) => {
                setSaving(false);
              });
          }
        }
      }
    }
  };

  const handleUnitChange = (event, title, attributeId) => {
    setUnit(event?.target?.value);
    setAttrId(attributeId);
    setDataSource((state) =>
      state.map((item) => {
        item[`${title}:${attributeId}`]?.length > 0
          ? (item[`${title}:${attributeId}`][2] = event?.target?.value)
          : (item[`${title}:${attributeId}`] = [
              null,
              null,
              event?.target?.value,
            ]);
        return item;
      })
    );
    //updateCurrencyOrUnit(attributeId, { unit: event?.target?.value }, title);
  };

  useEffect(() => {
    if (attrId && dataSource.length > 0) {
      updateCurrencyOrUnit(attrId, { unit, currency });
    }
  }, [attrId, dataSource, unit, currency]);

  const columnsObj = columns.map((col) => {
    if (!col.editable) {
      return col;
    }

    return {
      ...col,
      onCell: (record) => ({
        record,
        editable: col.editable,
        dataIndex: col.dataIndex,
        title: col.title,
        type: col.type,
        defaultValue: col.defaultValue,
        handleSave: handleSave,
        variantData: variantData,
        collectionProductId: collectionProductId,
        setSaving: setSaving,
        setIsSizeChanged: setIsSizeChanged,
        isSizeChanged,
      }),
    };
  });

  return (
    <Modal
      visible={isVariantModalVisible}
      onOk={handleOkVariant}
      onCancel={handleCancelVariant}
      footer={null}
      width={"80%"}
      centered
      bodyStyle={{ padding: "2em" }}
    >
      <Row>
        <Col>
          <Title level={4}>Variants available for {activeProductId}</Title>
        </Col>
        <Col>
          {saving ? (
            "Saving..."
          ) : (
            <IoCloudDoneOutline size={20} style={{ margin: 3 }} />
          )}
        </Col>
      </Row>

      <Row>
        <Col span={24}>
          <Table
            components={components}
            rowClassName={() => "editable-row"}
            bordered
            dataSource={dataSource}
            columns={columnsObj}
            pagination={false}
            // style={{height:400,overflowX:'scroll'}}
            // scroll={{x:'auto',y:'auto'}}
            // sticky
          />
        </Col>
      </Row>
    </Modal>
  );
}
